import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Type, Palette, Sparkles, Eye, Check, Layers, Wand2, User, MapPin, Globe, Linkedin, Instagram, Star, Download, X } from 'lucide-react';
import { THEME_PRESETS } from '../../data/themes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_FONTS = [
  '', 'Inter', 'Playfair Display', 'Roboto', 'Poppins', 'Montserrat',
  'Lora', 'Merriweather', 'Source Sans Pro', 'Oswald', 'Raleway',
  'Open Sans', 'Nunito', 'Work Sans', 'Quicksand', 'Crimson Text',
];

const TEXTURE_OPTIONS = [
  { id: 'none', label: 'None', desc: 'Clean background', free: true },
  { id: 'grain', label: 'Film Grain', desc: 'Subtle noise texture', free: true },
  { id: 'dots', label: 'Dot Grid', desc: 'Faint dot pattern', free: true },
  { id: 'mesh', label: 'Mesh Gradient', desc: 'Smooth color blends', free: false },
  { id: 'lines', label: 'Diagonal Lines', desc: 'Geometric line pattern', free: false },
  { id: 'aurora', label: 'Aurora', desc: 'Animated color waves', free: false },
];

const EFFECT_OPTIONS = [
  { id: 'none', label: 'None', desc: 'No extra effects', free: true },
  { id: 'glow', label: 'Photo Glow', desc: 'Accent glow on profile photo', free: false },
  { id: 'glass', label: 'Glass-morphism', desc: 'Frosted glass on sections', free: false },
  { id: 'border-glow', label: 'Gradient Border', desc: 'Animated gradient borders', free: false },
];

const ColorPicker = ({ label, value, onChange, disabled }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-gray-500">{label}</label>
    <div className="flex items-center gap-2">
      <div className="relative w-10 h-10 rounded-lg overflow-hidden border border-gray-200 shadow-sm" style={{ background: value }}>
        <input type="color" value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
      </div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
        placeholder="#000000" />
    </div>
  </div>
);

/* ========= LIVE CARD PREVIEW ========= */
const LiveCardPreview = ({ theme, texture, effect, font, profile }) => {
  const t = theme;
  const isDark = t.bg_color && !['#ffffff', '#f0fdf4', '#fffbeb'].includes(t.bg_color?.toLowerCase());
  const subtleBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const subtleBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
  const mutedText = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)';

  const textureClass = texture && texture !== 'none' ? `card-texture-${texture}` : '';
  const effectClass = effect && effect !== 'none' ? `card-effect-${effect}` : '';

  const meshVars = {
    '--mesh-color-1': `${t.accent_color || '#6366f1'}66`,
    '--mesh-color-2': `${t.primary_color || '#a855f7'}44`,
    '--glow-color': `${t.accent_color || '#6366f1'}55`,
    '--glow-color-2': `${t.primary_color || '#a855f7'}44`,
  };

  const fontStyle = font ? { fontFamily: `'${font}', sans-serif` } : {};
  const p = profile?.profile || {};
  const name = p.name || 'Your Name';
  const links = profile?.links?.slice(0, 6) || [
    { name: 'Portfolio', icon: 'globe' }, { name: 'LinkedIn', icon: 'linkedin' }, { name: 'Instagram', icon: 'instagram' },
  ];

  const PREVIEW_ICONS = { globe: Globe, linkedin: Linkedin, instagram: Instagram };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden ${textureClass} ${effectClass}`}
      style={{ background: t.bg_color || '#09090b', color: t.text_color || '#fafafa', ...fontStyle, ...meshVars, minHeight: '480px' }}
      data-testid="live-preview"
    >
      {/* Cover */}
      {p.cover_image_url && (
        <div className="h-20 overflow-hidden">
          <img src={p.cover_image_url} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="relative z-10 p-5 flex flex-col items-center">
        {/* Profile Photo */}
        <div className={`w-20 h-20 rounded-full overflow-hidden ring-4 card-profile-photo ${p.cover_image_url ? '-mt-10' : ''}`}
          style={{ borderColor: subtleBorder, backgroundColor: t.bg_color, ringColor: t.bg_color }}>
          {p.image_url ? (
            <img src={p.image_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: subtleBg }}>
              <User className="w-8 h-8" style={{ color: mutedText }} />
            </div>
          )}
        </div>

        {/* Info */}
        <h2 className="font-heading text-lg font-medium mt-3" style={{ color: t.name_color || t.text_color }}>{name}</h2>
        {p.title && <p className="text-xs font-semibold mt-0.5" style={{ color: t.accent_color || t.primary_color }}>{p.title}</p>}
        {p.company && <p className="text-[11px]" style={{ color: mutedText }}>{p.company}</p>}
        {p.location && (
          <div className="flex items-center gap-1 mt-1.5" style={{ color: mutedText }}>
            <MapPin className="w-2.5 h-2.5" />
            <span className="text-[10px]">{p.location}</span>
          </div>
        )}

        {/* Contact Form Preview */}
        <div className="w-full mt-5 rounded-xl p-4 card-form-section" style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}>
          <p className="text-[9px] font-semibold uppercase tracking-wider text-center mb-3" style={{ color: mutedText }}>Share your info</p>
          <div className="space-y-2">
            <div className="rounded-lg py-2 px-3 text-[11px]" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: mutedText }}>
              Full name
            </div>
            <div className="rounded-lg py-2 px-3 text-[11px]" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: mutedText }}>
              Phone number
            </div>
          </div>
          <button className="w-full py-2 rounded-full font-semibold text-[11px] mt-3"
            style={{ background: t.primary_color, color: isDark ? t.bg_color : '#ffffff' }}>
            Connect
          </button>
        </div>

        {/* Links Preview */}
        <div className="w-full mt-4">
          <button className="w-full py-2 rounded-full font-bold uppercase tracking-wider text-[10px] mb-4 card-links-section"
            style={{ background: t.primary_color, color: isDark ? t.bg_color : '#ffffff' }}>
            Save Contact
          </button>
          <div className="grid grid-cols-3 gap-2">
            {links.slice(0, 3).map((link, idx) => {
              const Icon = PREVIEW_ICONS[link.icon] || Globe;
              return (
                <div key={idx} className="flex flex-col items-center gap-1.5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center card-links-section"
                    style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}>
                    <Icon className="w-4 h-4" style={{ color: t.primary_color }} />
                  </div>
                  <span className="text-[9px] font-medium" style={{ color: mutedText }}>{link.name || link.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonial Preview */}
        {profile?.testimonials?.length > 0 && (
          <div className="w-full mt-4 p-3 rounded-xl card-links-section" style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}>
            <div className="flex gap-0.5 mb-1.5">
              {[1,2,3,4,5].map(s => <Star key={s} className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />)}
            </div>
            <p className="text-[10px] leading-relaxed" style={{ color: t.text_color }}>"{profile.testimonials[0].text?.substring(0, 60)}..."</p>
            <p className="text-[9px] font-semibold mt-1.5" style={{ color: t.text_color }}>{profile.testimonials[0].name}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ========= MAIN THEME TAB ========= */
const ThemeTab = ({ profile, setProfile, showMsg }) => {
  const [selected, setSelected] = useState(profile.theme?.preset || 'obsidian');
  const [customFont, setCustomFont] = useState(profile.theme?.custom_font || '');
  const [isCustomMode, setIsCustomMode] = useState(profile.theme?.preset === 'custom');
  const [saving, setSaving] = useState(false);
  const [cardTexture, setCardTexture] = useState(profile.theme?.card_texture || 'none');
  const [cardEffect, setCardEffect] = useState(profile.theme?.card_effect || 'none');
  const [showPreview, setShowPreview] = useState(false);

  const [customTheme, setCustomTheme] = useState({
    bg_color: profile.theme?.bg_color || '#09090b',
    text_color: profile.theme?.text_color || '#fafafa',
    name_color: profile.theme?.name_color || '#ffffff',
    primary_color: profile.theme?.primary_color || '#ffffff',
    accent_color: profile.theme?.accent_color || '#a1a1aa',
  });

  const isPro = profile.plan !== 'free';

  useEffect(() => {
    if (isCustomMode && profile.theme) {
      setCustomTheme({
        bg_color: profile.theme.bg_color || '#09090b',
        text_color: profile.theme.text_color || '#fafafa',
        name_color: profile.theme.name_color || '#ffffff',
        primary_color: profile.theme.primary_color || '#ffffff',
        accent_color: profile.theme.accent_color || '#a1a1aa',
      });
    }
  }, [isCustomMode, profile.theme]);

  const currentPreviewTheme = isCustomMode ? { preset: 'custom', ...customTheme } : (THEME_PRESETS[selected] || THEME_PRESETS.obsidian);

  const buildThemeData = (overrides = {}) => ({
    ...(isCustomMode ? { preset: 'custom', ...customTheme } : THEME_PRESETS[selected]),
    custom_font: customFont || null,
    card_texture: cardTexture || null,
    card_effect: cardEffect || null,
    ...overrides,
  });

  const saveTheme = async (preset) => {
    setSelected(preset);
    setIsCustomMode(false);
    setSaving(true);
    try {
      const themeData = { ...THEME_PRESETS[preset], custom_font: customFont || null, card_texture: cardTexture || null, card_effect: cardEffect || null };
      const res = await axios.put(`${API}/profile/theme`, { theme: themeData });
      setProfile(prev => ({ ...prev, theme: res.data.theme }));
      showMsg('Theme updated');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to update theme');
    } finally { setSaving(false); }
  };

  const saveCustomTheme = async () => {
    if (!isPro) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile/theme`, { theme: buildThemeData({ preset: 'custom' }) });
      setProfile(prev => ({ ...prev, theme: res.data.theme }));
      showMsg('Custom theme saved');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to save theme');
    } finally { setSaving(false); }
  };

  const saveFont = async (font) => {
    setCustomFont(font);
    try {
      const res = await axios.put(`${API}/profile/theme`, { theme: buildThemeData({ custom_font: font || null }) });
      setProfile(prev => ({ ...prev, theme: res.data.theme }));
      showMsg('Font updated');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to update font');
    }
  };

  const saveTextureOrEffect = async (type, value) => {
    if (type === 'texture') setCardTexture(value);
    else setCardEffect(value);
    try {
      const overrides = type === 'texture' ? { card_texture: value } : { card_effect: value };
      const res = await axios.put(`${API}/profile/theme`, { theme: buildThemeData(overrides) });
      setProfile(prev => ({ ...prev, theme: res.data.theme }));
      showMsg(type === 'texture' ? 'Texture updated' : 'Effect updated');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to update');
    }
  };

  const updateCustomColor = (key, value) => {
    setCustomTheme(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div data-testid="theme-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-heading text-2xl font-normal text-gray-900">Card Theme</h2>
        {/* Mobile preview toggle */}
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="lg:hidden flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-900 text-white"
          data-testid="toggle-preview-btn"
        >
          <Eye className="w-3.5 h-3.5" /> {showPreview ? 'Hide Preview' : 'Live Preview'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">Choose a preset theme or create your own custom design</p>

      {/* Mobile Preview Panel */}
      {showPreview && (
        <div className="lg:hidden mb-6 relative" data-testid="mobile-preview-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Live Preview</span>
            <button onClick={() => setShowPreview(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="max-w-xs mx-auto">
            <LiveCardPreview theme={currentPreviewTheme} texture={cardTexture} effect={cardEffect} font={customFont} profile={profile} />
          </div>
        </div>
      )}

      <div className="flex gap-8">
        {/* Settings Column */}
        <div className="flex-1 min-w-0">
          {/* Theme Presets */}
          <div className="mb-8">
            <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Palette className="w-4 h-4 text-gray-400" /> Theme Presets
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {Object.entries(THEME_PRESETS).map(([key, theme]) => {
                const locked = !isPro && !['obsidian', 'paper'].includes(key);
                const isSelected = selected === key && !isCustomMode;
                return (
                  <button key={key} onClick={() => !locked && saveTheme(key)}
                    className={`relative rounded-2xl p-5 border-2 transition-all text-left ${isSelected ? 'border-indigo-600 shadow-lg shadow-indigo-600/5' : 'border-gray-200 hover:border-gray-300'} ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{ background: theme.bg_color }} data-testid={`theme-${key}`}>
                    <div className="flex gap-1.5 mb-3">
                      <div className="w-5 h-5 rounded-full border border-white/20" style={{ background: theme.primary_color }}></div>
                      <div className="w-5 h-5 rounded-full border border-white/20" style={{ background: theme.accent_color }}></div>
                    </div>
                    <p className="text-sm font-semibold" style={{ color: theme.name_color || theme.text_color }}>{theme.label}</p>
                    {locked && <span className="absolute top-2.5 right-2.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">PRO</span>}
                    {isSelected && <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Theme Builder */}
          <div className="pt-8 border-t border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" /> Custom Theme Builder
                {!isPro && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">PRO</span>}
              </h3>
              {isPro && (
                <button onClick={() => setIsCustomMode(true)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${isCustomMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {isCustomMode ? 'Custom Mode Active' : 'Enable Custom Mode'}
                </button>
              )}
            </div>

            {!isPro ? (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 text-center">
                <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <p className="font-semibold text-gray-900 mb-1">Unlock Full Customization</p>
                <p className="text-sm text-gray-600 mb-4">Upgrade to Pro to create your own unique theme with custom colors</p>
                <button onClick={() => window.location.href = '/dashboard?tab=plan'}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm px-6 py-2.5 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors">
                  Upgrade to Pro
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <ColorPicker label="Background Color" value={customTheme.bg_color} onChange={v => updateCustomColor('bg_color', v)} disabled={!isCustomMode} />
                <ColorPicker label="Name Color" value={customTheme.name_color} onChange={v => updateCustomColor('name_color', v)} disabled={!isCustomMode} />
                <ColorPicker label="Text Color" value={customTheme.text_color} onChange={v => updateCustomColor('text_color', v)} disabled={!isCustomMode} />
                <ColorPicker label="Primary Color (Buttons)" value={customTheme.primary_color} onChange={v => updateCustomColor('primary_color', v)} disabled={!isCustomMode} />
                <ColorPicker label="Accent Color (Title, Links)" value={customTheme.accent_color} onChange={v => updateCustomColor('accent_color', v)} disabled={!isCustomMode} />
                {isCustomMode && (
                  <button onClick={saveCustomTheme} disabled={saving}
                    className="w-full mt-4 bg-indigo-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="save-custom-theme">
                    {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Save className="w-4 h-4" /> Save Custom Theme</>}
                  </button>
                )}
                {!isCustomMode && <p className="text-xs text-gray-400 text-center mt-2">Enable Custom Mode to edit colors</p>}
              </div>
            )}
          </div>

          {/* Custom Font */}
          <div className="mt-10 pt-8 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <Type className="w-5 h-5 text-gray-400" />
              <h3 className="text-sm font-bold text-gray-700">Custom Font</h3>
              {!isPro && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">PRO</span>}
            </div>
            <select value={customFont} onChange={e => isPro && saveFont(e.target.value)} disabled={!isPro}
              className={`w-full max-w-xs bg-white border border-gray-200 rounded-lg text-gray-900 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-100 ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ fontFamily: customFont || 'inherit' }} data-testid="font-select">
              <option value="">Default System Font</option>
              {GOOGLE_FONTS.filter(f => f).map(font => <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>)}
            </select>
            {customFont && <p className="text-xs text-gray-400 mt-2" style={{ fontFamily: customFont }}>Preview: The quick brown fox jumps over the lazy dog</p>}
          </div>

          {/* Background Texture */}
          <div className="mt-10 pt-8 border-t border-gray-100">
            <h3 className="text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
              <Layers className="w-4 h-4 text-gray-400" /> Background Texture
            </h3>
            <p className="text-xs text-gray-400 mb-4">Add depth with a background pattern</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {TEXTURE_OPTIONS.map(opt => {
                const locked = !opt.free && !isPro;
                const isActive = cardTexture === opt.id;
                return (
                  <button key={opt.id} onClick={() => !locked && saveTextureOrEffect('texture', opt.id)}
                    className={`relative text-left rounded-xl p-3.5 border-2 transition-all ${isActive ? 'border-indigo-600 bg-indigo-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'} ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`texture-${opt.id}`}>
                    <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</p>
                    {locked && <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">PRO</span>}
                    {isActive && <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card Effects */}
          <div className="mt-10 pt-8 border-t border-gray-100">
            <h3 className="text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-gray-400" /> Card Effects
            </h3>
            <p className="text-xs text-gray-400 mb-4">Premium visual effects for your card</p>
            <div className="grid grid-cols-2 gap-3">
              {EFFECT_OPTIONS.map(opt => {
                const locked = !opt.free && !isPro;
                const isActive = cardEffect === opt.id;
                return (
                  <button key={opt.id} onClick={() => !locked && saveTextureOrEffect('effect', opt.id)}
                    className={`relative text-left rounded-xl p-3.5 border-2 transition-all ${isActive ? 'border-indigo-600 bg-indigo-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'} ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`effect-${opt.id}`}>
                    <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</p>
                    {locked && <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">PRO</span>}
                    {isActive && <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop Sticky Preview */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-20">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500">Live Preview</span>
            </div>
            <LiveCardPreview theme={currentPreviewTheme} texture={cardTexture} effect={cardEffect} font={customFont} profile={profile} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeTab;
